import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import LoginPage from './pages/LoginPage.jsx';
import Layout from './layout';

// Import Inter font
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import Patients from './components/Patients.jsx';
import Doctors from './components/Doctors.jsx';
import PreHADSScreening from './components/PreHADSScreening.jsx';
import TreatmentSessions from './components/TreatmentSessions.jsx';
import ViewPin from './components/ViewPatientPin.jsx';
import MyAssesments from './components/MyAssesments.jsx';
import AssessmentForm from './components/AssessmentForm.jsx';
import MyProfile from './components/MyProfile.jsx';
import MusicTherapy from './components/MusicTherapy.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';
import ResetPassword from './pages/ResetPassword.jsx';
import { useEffect, useState } from 'react';
import { Box, CircularProgress } from "@mui/material";
import TodaysPatients from './components/TodaysPatients.jsx';
import AnalyticsPage from './components/Analytics.jsx';
import ApolloPatients from './components/ApolloPatients.jsx';
import ApolloTreatmentSessions from './components/ApolloTreatmentSessions.jsx';
import ApolloMyAssesments from './components/ApolloMyAssesments.jsx';
import ApolloMusicTherapy from './components/ApolloMusicTherapy.jsx';
import AppointmentBooking from './components/AppointmentBooking.jsx';
import ConsultLoginPage from './pages/ConsultLogin.jsx';
import MyAppointments from './components/Appointment.jsx';
import ConsultForgotPassword from './pages/ConsultForgotPassword.jsx';
import AppointmentCustomerProfile from './components/AppointmentCustomerProfile.jsx';
import { Outlet } from "react-router-dom";
import { ProviderLanguageProvider } from './i18n/ProviderLanguageContext.jsx';

function ConditionalPatients() {
  const user = JSON.parse(localStorage.getItem("user"));
  const account_id = user?.account_id;
  console.log("account_id in app.jsx", account_id)
  if (account_id === 1) {
    return <ApolloPatients />;
  }
  return <Patients />;
}

function ConditionalTreatmentSessions() {
  const user = JSON.parse(localStorage.getItem("user"));
  const account_id = user?.account_id;
  if (account_id === 1) {
    return <ApolloTreatmentSessions />;
  }
  return <TreatmentSessions />;
}

function ConditionalMyAssesments() {
  const customer = JSON.parse(localStorage.getItem("customer"));
  const account_id = Number(customer?.account_id);

  if (account_id === 1) {
    return <ApolloMyAssesments />;
  }
  return <MyAssesments />;
}

function ConditionalMusicTherapy() {
  const customer = JSON.parse(localStorage.getItem("customer"));

  if (!customer) return <MusicTherapy />; // fallback

  const account_id = Number(customer.account_id);

  if (account_id === 1) return <ApolloMusicTherapy />;
  return <MusicTherapy />;
}



// AuthRoute Component for role + message based route protection
const AuthRoute = ({ allowedPortal }) => {
  const location = useLocation();
  const role = localStorage.getItem('role');
  const message = localStorage.getItem('loginMessage');

  if (!role) {
    // Not logged in
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (role === 'user' && allowedPortal === 'provider') {
    return <Outlet />;
  }

  if (
    role === 'customer' &&
    message === 'Login successful' &&
    allowedPortal === 'appointment'
  ) {
    return <Outlet />;
  }

  if (
    role === 'customer' &&
    message === 'PIN validated successfully' &&
    allowedPortal === 'patient'
  ) {
    return <Outlet />;
  }

  // Unauthorized - redirect to appropriate login pages:
  if (role === 'user') {
    return <Navigate to="/" replace />;
  }
  if (role === 'customer' && message === 'Login successful') {
    return <Navigate to="/appointment/login" replace />;
  }
  if (role === 'customer' && message === 'PIN validated successfully') {
    return <Navigate to="/my-assessments" replace />;
  }

  return <Navigate to="/" replace />;
};
// const AnyAuthRoute = () => {
//   const role = localStorage.getItem("role");
//   const message = localStorage.getItem("loginMessage");

//   // 1️⃣ Provider portal → allow
//   if (role === "user") {
//     return <Outlet />;
//   }

//   // 2️⃣ Patient portal after PIN validation → allow
//   if (
//     role === "customer" &&
//     message === "PIN validated successfully"
//   ) {
//     return <Navigate to="/my-assessments" replace />;
//   }

//   // 3️⃣ Consult login successful → allow
//   if (
//     role === "customer" &&
//     message === "Customer login successful"
//   ) {
//     return <Outlet />;
//   }

//   // ❌ Not allowed → redirect
//   return <Navigate to="/" replace />;
// }
const AnyAuthRoute = () => {
  const role = localStorage.getItem("role");
  const message = localStorage.getItem("loginMessage");

  if (!role) {
    return <Navigate to="/" replace />;
  }

  // Allow provider
  if (role === "user") {
    return <Outlet />;
  }

  // Allow customer after PIN
  if (role === "customer" && message === "PIN validated successfully") {
    return <Outlet />;
  }

  // Allow customer login
  if (role === "customer" && message === 'Login successful') {
    return <Outlet />;
  }

  return <Navigate to="/" replace />;
};




const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#4C41FF',
    },
    secondary: {
      main: '#FF4081',
    },
    background: {
      default: '#f8fafc',
    },
    text: {
      primary: '#1F2937',
      secondary: '#64748B',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 8px 20px rgba(76, 65, 255, 0.35)',
          },
        },
        contained: {
          background: 'linear-gradient(90deg, #7aa7ff, #7e94ff, #8aa2ff)',
          '&:hover': {
            background: 'linear-gradient(90deg, #6a97ff, #6e84ff, #7a92ff)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 24px 40px rgba(0, 0, 0, 0.12)',
          border: '1px solid rgba(0,0,0,0.05)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 24px 40px rgba(0, 0, 0, 0.12)',
        },
      },
    },
  },
});



function App() {
  const [authReady, setAuthReady] = useState(false);
  useEffect(() => {
    // wait one tick for localStorage to be available
    setAuthReady(true);
  }, []);
  if (!authReady) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (

    <ThemeProvider theme={theme}>
      <CssBaseline />

        <ProviderLanguageProvider>
      {/* <SnackbarProvider> */}
      {/* <BrowserRouter> */}
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="/forgotpassword" element={<ForgotPassword />} />
        <Route path="/consult/forgotpassword" element={<ConsultForgotPassword />} />
        <Route path="/resetPassword" element={<ResetPassword />} />
        <Route path="/appointment/login" element={<ConsultLoginPage />} />

        {/* Dashboard with Layout */}
        {/* <Route path="/patients" element={<Layout />}>
          <Route index element={<Patients />} />
        </Route> */}
        <Route element={<AuthRoute allowedPortal="provider" />}>
          <Route path="/patients" element={<Layout />}>
            <Route index element={<ConditionalPatients />} />
          </Route>

          <Route path="/doctors" element={<Layout />}>
            <Route index element={<Doctors />} />
          </Route>

          <Route path="/prehadsscreening" element={<Layout />}>
            <Route index element={<PreHADSScreening />} />
          </Route>

          {/* <Route path="/treatmentsessions" element={<Layout />}>
          <Route index element={<TreatmentSessions />} />
        </Route> */}

          <Route path="/treatmentsessions" element={<Layout />}>
            <Route index element={<ConditionalTreatmentSessions />} />
          </Route>

          <Route path="/view-pin" element={<Layout />}>
            <Route index element={<ViewPin />} />
          </Route>

          {/* <Route path="/my-assessments" element={<Layout />}>
          <Route index element={<MyAssesments />} />
        </Route> */}


          <Route path="/todays-patient" element={<Layout />}>
            <Route index element={<TodaysPatients />} />
          </Route>

          <Route path="/analytics" element={<Layout />}>
            <Route index element={<AnalyticsPage />} />
          </Route>
        </Route>




        {/* <Route path="/music-therapy" element={<Layout />}>
          <Route index element={<MusicTherapy />} />
        </Route> */}



        {/* <Route element={<AnyAuthRoute />}>
          <Route path="/my-profile" element={<Layout />}>
            <Route index element={<MyProfile />} />
          </Route>

          <Route element={<AuthRoute allowedPortal="csutomer" />}>
          </Route>
          <Route path="/my-assessments" element={<Layout />}>
            <Route index element={<ConditionalMyAssesments />} />
          </Route>

          <Route path="/assessment" element={<Layout />}>
            <Route index element={<AssessmentForm />} />
          </Route>

          <Route path="/music-therapy" element={<Layout />}>
            <Route index element={<ConditionalMusicTherapy />} />
          </Route>
        </Route> */}
        <Route element={<AnyAuthRoute />}>
          <Route path="/my-assessments" element={<Layout />}>
            <Route index element={<ConditionalMyAssesments />} />
          </Route>

          <Route path="/assessment" element={<Layout />}>
            <Route index element={<AssessmentForm />} />
          </Route>

          <Route path="/music-therapy" element={<Layout />}>
            <Route index element={<ConditionalMusicTherapy />} />
          </Route>
        </Route>
        <Route path="/my-profile" element={<Layout />}>
          <Route index element={<MyProfile />} />
        </Route>


        <Route element={<AuthRoute allowedPortal="appointment" />}>

          <Route path="/appointment/booking" element={<Layout />}>
            <Route index element={<AppointmentBooking />} />
          </Route>

          <Route path="/appointment/my-appointments" element={<Layout />}>
            <Route index element={<MyAppointments />} />
          </Route>
          <Route path="/appointment/my-profile" element={<Layout />}>
            <Route index element={<AppointmentCustomerProfile />} />
          </Route>
        </Route>
      </Routes>

       </ProviderLanguageProvider>



      {/* </BrowserRouter> */}
      {/* </SnackbarProvider> */}

    </ThemeProvider>
  );
}

export default App;

// import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
// import { ThemeProvider, createTheme } from '@mui/material/styles';
// import CssBaseline from '@mui/material/CssBaseline';
// import LoginPage from './pages/LoginPage.jsx';
// import Layout from './layout';

// // Import Inter font
// import '@fontsource/inter/300.css';
// import '@fontsource/inter/400.css';
// import '@fontsource/inter/500.css';
// import '@fontsource/inter/600.css';
// import '@fontsource/inter/700.css';
// import Patients from './components/Patients.jsx';
// import Doctors from './components/Doctors.jsx';
// import PreHADSScreening from './components/PreHADSScreening.jsx';
// import TreatmentSessions from './components/TreatmentSessions.jsx';
// import ViewPin from './components/ViewPatientPin.jsx';
// import MyAssesments from './components/MyAssesments.jsx';
// import AssessmentForm from './components/AssessmentForm.jsx';
// import MyProfile from './components/MyProfile.jsx';
// import MusicTherapy from './components/MusicTherapy.jsx';
// import ForgotPassword from './pages/ForgotPassword.jsx';
// import ResetPassword from './pages/ResetPassword.jsx';
// import { useEffect, useState } from 'react';
// import { Box, CircularProgress } from "@mui/material";
// import TodaysPatients from './components/TodaysPatients.jsx';
// import AnalyticsPage from './components/Analytics.jsx';
// import ApolloPatients from './components/ApolloPatients.jsx';
// import ApolloTreatmentSessions from './components/ApolloTreatmentSessions.jsx';
// import ApolloMyAssesments from './components/ApolloMyAssesments.jsx';
// import ApolloMusicTherapy from './components/ApolloMusicTherapy.jsx';
// import ConsultLogin from './pages/ConsultLogin.jsx';
// import AppoimentBooking from './components/AppoimentBooking.jsx';
// import Appointment from './components/Appointment.jsx';
// import { Navigate, Outlet } from "react-router-dom";


// function ConditionalPatients() {
//   const user = JSON.parse(localStorage.getItem("user"));
//   const account_id = user?.account_id;
//   console.log("account_id in app.jsx", account_id)
//   if (account_id === 1) {
//     return <ApolloPatients />;
//   }
//   return <Patients />;
// }

// function ConditionalTreatmentSessions() {
//   const user = JSON.parse(localStorage.getItem("user"));
//   const account_id = user?.account_id;
//   if (account_id === 1) {
//     return <ApolloTreatmentSessions />;
//   }
//   return <TreatmentSessions />;
// }
// function ConditionalMyAssesments() {
//   const customer = JSON.parse(localStorage.getItem("customer"));
//   const account_id = Number(customer?.account_id);

//   if (account_id === 1) {
//     return <ApolloMyAssesments />;
//   }
//   return <MyAssesments />;
// }

// function ConditionalMusicTherapy() {
//   const customer = JSON.parse(localStorage.getItem("customer"));

//   if (!customer) return <MusicTherapy />; // fallback

//   const account_id = Number(customer.account_id);

//   if (account_id === 1) return <ApolloMusicTherapy />;
//   return <MusicTherapy />;
// }

// // AuthRoute Component for role + message based route protection
// const AuthRoute = ({ allowedPortal }) => {
//   const location = useLocation();
//   const role = localStorage.getItem('role');
//   const message = localStorage.getItem('loginMessage');

//   if (!role) {
//     // Not logged in
//     return <Navigate to="/" replace state={{ from: location }} />;
//   }

//   if (role === 'user' && allowedPortal === 'provider') {
//     return <Outlet />;
//   }

//   if (
//     role === 'customer' &&
//     message === 'Customer login successful' &&
//     allowedPortal === 'appointment'
//   ) {
//     return <Outlet />;
//   }

//   if (
//     role === 'customer' &&
//     message === 'PIN validated successfully' &&
//     allowedPortal === 'patient'
//   ) {
//     return <Outlet />;
//   }

//   // Unauthorized - redirect to appropriate login pages:
//   if (role === 'user') {
//     return <Navigate to="/" replace />;
//   }
//   if (role === 'customer' && message === 'Customer login successful') {
//     return <Navigate to="/appointment/login" replace />;
//   }
//   if (role === 'customer' && message === 'PIN validated successfully') {
//     return <Navigate to="/" replace />;
//   }

//   return <Navigate to="/" replace />;
// };
// const AnyAuthRoute = () => {
//   const role = localStorage.getItem("role");
//   const message = localStorage.getItem("loginMessage");

//   // 1️⃣ Provider portal → allow
//   if (role === "user") {
//     return <Outlet />;
//   }

//   // 2️⃣ Patient portal after PIN validation → allow
//   if (
//     role === "customer" &&
//     message === "PIN validated successfully"
//   ) {
//     return <Outlet />;
//   }

//   // 3️⃣ Consult login successful → allow
//   if (
//     role === "customer" &&
//     message === "Customer login successful"
//   ) {
//     return <Outlet />;
//   }

//   // ❌ Not allowed → redirect
//   return <Navigate to="/" replace />;
// };


// const theme = createTheme({
//   palette: {
//     mode: 'light',
//     primary: {
//       main: '#4C41FF',
//     },
//     secondary: {
//       main: '#FF4081',
//     },
//     background: {
//       default: '#f8fafc',
//     },
//     text: {
//       primary: '#1F2937',
//       secondary: '#64748B',
//     },
//   },
//   typography: {
//     fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
//     h1: {
//       fontWeight: 700,
//     },
//     h2: {
//       fontWeight: 600,
//     },
//     h3: {
//       fontWeight: 600,
//     },
//     h4: {
//       fontWeight: 600,
//     },
//     button: {
//       fontWeight: 600,
//       textTransform: 'none',
//     },
//   },
//   components: {
//     MuiCssBaseline: {
//       styleOverrides: {
//         html: {
//           fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
//         },
//       },
//     },
//     MuiButton: {
//       styleOverrides: {
//         root: {
//           borderRadius: 8,
//           textTransform: 'none',
//           fontWeight: 600,
//           boxShadow: 'none',
//           '&:hover': {
//             boxShadow: '0 8px 20px rgba(76, 65, 255, 0.35)',
//           },
//         },
//         contained: {
//           background: 'linear-gradient(90deg, #7aa7ff, #7e94ff, #8aa2ff)',
//           '&:hover': {
//             background: 'linear-gradient(90deg, #6a97ff, #6e84ff, #7a92ff)',
//           },
//         },
//       },
//     },
//     MuiCard: {
//       styleOverrides: {
//         root: {
//           borderRadius: 16,
//           boxShadow: '0 24px 40px rgba(0, 0, 0, 0.12)',
//           border: '1px solid rgba(0,0,0,0.05)',
//         },
//       },
//     },
//     MuiPaper: {
//       styleOverrides: {
//         root: {
//           borderRadius: 16,
//           boxShadow: '0 24px 40px rgba(0, 0, 0, 0.12)',
//         },
//       },
//     },
//   },
// });



// function App() {
//    const [authReady, setAuthReady] = useState(false);
//     useEffect(() => {
//     // wait one tick for localStorage to be available
//     setAuthReady(true);
//   }, []);
//    if (!authReady) {
//     return (
//       <Box
//         sx={{
//           height: "100vh",
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "center",
//         }}
//       >
//         <CircularProgress />
//       </Box>
//     );
//   }

//   return (


//     <ThemeProvider theme={theme}>
//       <CssBaseline />
//       {/* <SnackbarProvider> */}
//       {/* <BrowserRouter> */}
//       <Routes>
//         <Route path="/" element={<LoginPage />} />
//         <Route path="/forgotpassword" element={<ForgotPassword />} />
//         <Route path="/resetPassword" element={<ResetPassword />} />
//         <Route path="/appointment/login" element={<ConsultLogin />} />


//         {/* Dashboard with Layout */}

//           <Route element={<AuthRoute allowedPortal="provider" />}>
//           <Route path="/patients" element={<Layout />}>
//             <Route index element={<ConditionalPatients />} />
//           </Route>

//           <Route path="/doctors" element={<Layout />}>
//             <Route index element={<Doctors />} />
//           </Route>

//           <Route path="/prehadsscreening" element={<Layout />}>
//             <Route index element={<PreHADSScreening />} />
//           </Route>

//           <Route path="/treatmentsessions" element={<Layout />}>
//             <Route index element={<ConditionalTreatmentSessions />} />
//           </Route>

//           <Route path="/view-pin" element={<Layout />}>
//             <Route index element={<ViewPin />} />
//           </Route>



//           <Route path="/todays-patient" element={<Layout />}>
//             <Route index element={<TodaysPatients />} />
//           </Route>

//           <Route path="/analytics" element={<Layout />}>
//             <Route index element={<AnalyticsPage />} />
//           </Route>
//         </Route>


//      <Route element={<AnyAuthRoute />}>
//   <Route path="/my-profile" element={<Layout />}>
//     <Route index element={<MyProfile />} />
//   </Route>
// </Route>



//              <Route element={<AuthRoute allowedPortal="patient" />}>

//           <Route path="/my-assessments" element={<Layout />}>
//             <Route index element={<ConditionalMyAssesments />} />
//           </Route>



//           <Route path="/assessment" element={<Layout />}>
//             <Route index element={<AssessmentForm />} />
//           </Route>




//           <Route path="/music-therapy" element={<Layout />}>
//             <Route index element={<ConditionalMusicTherapy />} />
//           </Route>
//         </Route>



//         <Route element={<AuthRoute allowedPortal="appointment" />}>
//           <Route path="/appointment/booking" element={<Layout />}>
//             <Route index element={<AppoimentBooking />} />
//           </Route>
//           <Route path="/appointment/my-appointments" element={<Layout />}>
//             <Route index element={<Appointment />} />
//           </Route>
//         </Route>



//       </Routes>
//       {/* </BrowserRouter> */}
//       {/* </SnackbarProvider> */}

//     </ThemeProvider>
//   );
// }

// export default App;




